import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API } from "@/lib/api";


export default function EventBudget({ eventId, isOwner }) {
  const [budget, setBudget] = useState({ total_budget: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    category: "",
    description: "",
    amount: 0,
    type: "expense"
  });

  useEffect(() => {
    fetchBudget();
  }, [eventId]);

  const fetchBudget = async () => {
    try {
      const response = await axios.get(`${API}/events/${eventId}/budget`, {
        withCredentials: true
      });
      setBudget(response.data);
    } catch (error) {
      console.error("Failed to fetch budget:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.amount) {
      toast.error("Kategorie und Betrag sind erforderlich!");
      return;
    }

    try {
      await axios.post(
        `${API}/events/${eventId}/budget/items`,
        formData,
        { withCredentials: true }
      );
      
      toast.success("Eintrag hinzugefügt!");
      setFormData({ category: "", description: "", amount: 0, type: "expense" });
      fetchBudget();
    } catch (error) {
      console.error("Failed to add item:", error);
      toast.error("Fehler beim Hinzufügen");
    }
  };

  const handleDelete = async (itemId) => {
    try {
      await axios.delete(
        `${API}/events/${eventId}/budget/items/${itemId}`,
        { withCredentials: true }
      );
      toast.success("Eintrag gelöscht");
      fetchBudget();
    } catch (error) {
      console.error("Failed to delete item:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const handleUpdateTotal = async () => {
    try {
      await axios.patch(
        `${API}/events/${eventId}/budget`,
        { total_budget: budget.total_budget },
        { withCredentials: true }
      );
      toast.success("Budget aktualisiert!");
    } catch (error) {
      console.error("Failed to update budget:", error);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Card>
    );
  }

  const totalExpenses = budget.items?.filter(i => i.type === 'expense').reduce((sum, i) => sum + i.amount, 0) || 0;
  const totalIncome = budget.items?.filter(i => i.type === 'income').reduce((sum, i) => sum + i.amount, 0) || 0;
  const balance = totalIncome - totalExpenses;
  const remaining = budget.total_budget - totalExpenses;

  if (!isOwner) {
    return (
      <Card className="p-6">
        <div className="text-center text-muted-foreground">
          <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Budget-Übersicht ist nur für Event-Ersteller sichtbar</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-primary" />
          Budget-Tracker
        </h3>

        {/* Budget Overview */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-secondary/20 rounded-lg">
            <Label className="text-xs text-muted-foreground">Geplantes Budget</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number"
                step="0.01"
                value={budget.total_budget}
                onChange={(e) => setBudget({ ...budget, total_budget: parseFloat(e.target.value) || 0 })}
                className="text-2xl font-bold p-1 h-auto"
              />
              <span className="text-2xl font-bold">€</span>
              <Button size="sm" onClick={handleUpdateTotal}>💾</Button>
            </div>
          </div>
          
          <div className="p-4 bg-secondary/20 rounded-lg">
            <p className="text-xs text-muted-foreground">Verbleibend</p>
            <p className={`text-2xl font-bold ${remaining < 0 ? 'text-destructive' : 'text-green-600'}`}>
              {remaining.toFixed(2)}€
            </p>
          </div>

          <div className="p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <p className="text-xs text-red-600">Ausgaben</p>
            </div>
            <p className="text-xl font-bold text-red-600">{totalExpenses.toFixed(2)}€</p>
          </div>

          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <p className="text-xs text-green-600">Einnahmen</p>
            </div>
            <p className="text-xl font-bold text-green-600">{totalIncome.toFixed(2)}€</p>
          </div>
        </div>
      </div>

      {/* Add Item Form */}
      <form onSubmit={handleSubmit} className="mb-6 p-4 border rounded-lg space-y-4 bg-secondary/20">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Typ</Label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 border rounded-md bg-background"
            >
              <option value="expense">Ausgabe</option>
              <option value="income">Einnahme</option>
            </select>
          </div>
          <div>
            <Label>Betrag (€)</Label>
            <Input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
              required
            />
          </div>
        </div>

        <div>
          <Label>Kategorie</Label>
          <Input
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            placeholder="z.B. Location, Catering, Dekoration"
            required
          />
        </div>

        <div>
          <Label>Beschreibung (optional)</Label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Zusätzliche Details..."
          />
        </div>

        <Button type="submit" className="w-full">
          <Plus className="w-4 h-4 mr-2" />
          Eintrag hinzufügen
        </Button>
      </form>

      {/* Items List */}
      <div className="space-y-2">
        <h4 className="font-medium text-sm mb-3">Einträge</h4>
        {budget.items?.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground py-4">Noch keine Einträge</p>
        ) : (
          budget.items?.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {item.type === 'expense' ? (
                    <TrendingDown className="w-4 h-4 text-red-600" />
                  ) : (
                    <TrendingUp className="w-4 h-4 text-green-600" />
                  )}
                  <span className="font-medium text-sm">{item.category}</span>
                </div>
                {item.description && (
                  <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className={`font-bold ${item.type === 'expense' ? 'text-red-600' : 'text-green-600'}`}>
                  {item.type === 'expense' ? '-' : '+'}{item.amount.toFixed(2)}€
                </span>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => handleDelete(item.id)}
                  className="h-8 w-8 text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}